import { useState } from 'react'
import { useSelector } from 'react-redux'
import { isDebtorType1 } from 'reducers/activecase/activecaseSlice'

import { useMutation } from '@apollo/client'
import {
  PLACE_NEW_COURSE_ORDER_CREATE_ACCOUNT,
  PlaceNewCourseOrderCreateAccountResponse,
  PlaceNewCourseOrderCreateAccountVariables
} from 'share/mutations/courses'
import { setCasePlaceOrderUpdateFields } from 'reducers/activecase/activecaseSlice'
import {
  HasPlacedCourseOrderStatusType,
  CourseOrderStep,
  getCasePlaceOrderUpdateFields
} from 'share/domain/case'
import { CourseType } from 'share/domain/course'
import { useAppDispatch, useAppSelector } from 'hooks/store'
import useLoadingDelay from 'hooks/useLoadingDelay'
import { loadingSpinnerPortal } from 'components/atoms/LoadingSpinner'
import {
  Formik,
  FastField,
  Form,
  FormikHelpers,
  FormikProps,
  ErrorMessage
} from 'formik'
import * as Yup from 'yup'
import Select from 'components/features/common/select/Select'
import Button from 'react-bootstrap/Button'
import './styles.scss'
import { dropdownStateOptions, phoneRegExp } from 'share/utils/utils'

interface Values {
  firstName: string
  lastName: string
  mobilePhone: string
  emailAddress: string
  mailingAddress: string
  zipCode: string
  city: string
  selectState: string
}

const validationSchema = Yup.object({
  firstName: Yup.string().required('First Name is Required'),
  lastName: Yup.string().required('Last Name is Required'),
  mobilePhone: Yup.string().matches(
    phoneRegExp,
    'Mobile phone number is not valid'
  ),
  emailAddress: Yup.string().email('Invalid email format').required('Required'),
  street: Yup.string(),
  zipCode: Yup.string(),
  city: Yup.string(),
  selectState: Yup.string().required('State selection required')
})

const CourseTabPlaceOrderCreateAccountForm = (): JSX.Element => {
  const isDebtor1 = useSelector(isDebtorType1)
  const dispatch = useAppDispatch()
  const authentication = useAppSelector((state) => state.authentication)
  const activeCase = useAppSelector((state) => state.activeCase)
  const activeCourse = useAppSelector((state) => state.activeCourse)
  const [submitError, setSubmitError] = useState('')
  const [createOrderAccount, { loading }] = useMutation<
    PlaceNewCourseOrderCreateAccountResponse,
    PlaceNewCourseOrderCreateAccountVariables
  >(PLACE_NEW_COURSE_ORDER_CREATE_ACCOUNT)

  async function createOrderAccountHelper(
    formValues: Values
  ): Promise<boolean> {
    let result = false

    const errorCasePlaceOrderUpdateFields = getCasePlaceOrderUpdateFields(
      isDebtor1,
      activeCourse?.activeTab === 'first',
      HasPlacedCourseOrderStatusType.ErrorOnCreateAccountStep,
      CourseOrderStep.CourseOrderFailed
    )

    try {
      const input = {
        caseId:
          activeCase.matter && activeCase.matter.id ? activeCase?.matter.id : 0,
        courseType:
          activeCourse?.activeTab === 'first'
            ? CourseType.CreditCounseling
            : CourseType.DebtorEducation,
        street: formValues.mailingAddress,
        city: formValues.city,
        stateCode: formValues.selectState,
        zipCode: formValues.zipCode,
        isDebtor1: isDebtor1
      }

      const createOrderAccountResult = await createOrderAccount({
        variables: { request: input }
      })

      if (
        !createOrderAccountResult.data?.orders.placeNewCourseOrderCreateAccount
          .success
      ) {
        setSubmitError('Error occurred while creating order account')
        console.log('error 1')
        console.dir(errorCasePlaceOrderUpdateFields)
        dispatch(setCasePlaceOrderUpdateFields(errorCasePlaceOrderUpdateFields))
        result = false
      } else {
        result = true

        dispatch(
          setCasePlaceOrderUpdateFields(
            createOrderAccountResult.data?.orders
              .placeNewCourseOrderCreateAccount
          )
        )
      }
    } catch (e) {
      setSubmitError('Error occurred while creating order account')
      result = false
      console.log('error 2')
      console.dir(errorCasePlaceOrderUpdateFields)
      dispatch(setCasePlaceOrderUpdateFields(errorCasePlaceOrderUpdateFields))
    }

    return result
  }

  const loadingDelay = useLoadingDelay(loading)

  const initialValues: Values = {
    firstName:
      authentication.account && authentication.account.givenName
        ? authentication.account.givenName
        : '',
    lastName:
      authentication.account && authentication.account.familyName
        ? authentication.account.familyName
        : '',
    mobilePhone:
      authentication.account && authentication.account.mobilePhone
        ? authentication.account.mobilePhone
        : '',
    emailAddress:
      authentication.account && authentication.account.emailAddress
        ? authentication.account.emailAddress
        : '',
    mailingAddress:
      authentication.account && authentication.account.street
        ? authentication.account.street
        : '',
    zipCode:
      authentication.account && authentication.account.zip
        ? authentication.account.zip
        : '',
    city:
      authentication.account && authentication.account.city
        ? authentication.account.city
        : '',
    selectState:
      authentication.account && authentication.account.state
        ? authentication.account.state
        : ''
  }

  function onSubmitWrapper(
    formik: FormikProps<Values>,
    e: React.FormEvent<HTMLFormElement>
  ) {
    e.preventDefault()
    formik.handleSubmit(e)
  }

  async function onSubmit(
    values: Values,
    { setSubmitting }: FormikHelpers<Values>
  ) {
    createOrderAccountHelper(values)
    setSubmitting(false)
  }

  return (
    <>
      {loadingDelay && loadingSpinnerPortal()}
      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={validationSchema}
      >
        {(formik) => {
          return (
            <Form
              className='form-container'
              onSubmit={(e) => onSubmitWrapper(formik, e)}
            >
              <div id='newAccountVerifyAccountDetails'>
                <div className='row'>
                  <div className='form-group col-12 col-sm-12 col-md'>
                    <label htmlFor='firstName'>First Name</label>
                    <FastField
                      id='firstName'
                      name='firstName'
                      data-testid='create-account-account-details-first-name-field'
                      placeholder=''
                      className={
                        formik.errors.firstName
                          ? 'form-control is-invalid'
                          : 'form-control'
                      }
                      autoFocus
                    />
                    <ErrorMessage
                      name='firstName'
                      render={(msg) => (
                        <div
                          className='text-danger'
                          data-testid='create-account-account-details-first-name-field-error'
                        >
                          {msg}
                        </div>
                      )}
                    />
                  </div>
                  <div className='form-group col-12 col-sm-12 col-md'>
                    <label htmlFor='lastName'>Last Name</label>
                    <FastField
                      type='text'
                      id='lastName'
                      name='lastName'
                      data-testid='create-account-account-details-last-name-field'
                      placeholder=''
                      className={
                        formik.errors.lastName
                          ? 'form-control is-invalid'
                          : 'form-control'
                      }
                    />
                    <ErrorMessage
                      name='lastName'
                      render={(msg) => (
                        <div
                          className='text-danger'
                          data-testid='create-account-account-details-last-name-field-error'
                        >
                          {msg}
                        </div>
                      )}
                    />
                  </div>
                </div>

                <div className='row'>
                  <div className='form-group col-md col-sm-12'>
                    <label htmlFor='mobilePhone'>Mobile Phone</label>
                    <FastField
                      readOnly
                      type='text'
                      id='mobilePhone'
                      name='mobilePhone'
                      data-testid='create-account-account-details-mobile-phone-field'
                      placeholder='Optional'
                      className='form-control'
                    />
                    <ErrorMessage
                      name='mobilePhone'
                      render={(msg) => (
                        <div
                          className='text-danger'
                          data-testid='create-account-account-details-mobile-phone-field-error'
                        >
                          {msg}
                        </div>
                      )}
                    />
                  </div>

                  <div className='form-group col-12 col-sm-12 col-md'>
                    <label htmlFor='emailAddress'>
                      Email Address (User ID)
                    </label>
                    <FastField
                      readOnly
                      type='email'
                      id='emailAddress'
                      name='emailAddress'
                      data-testid='create-account-account-details-email-address-field'
                      placeholder='Optional'
                      className='form-control'
                    />
                    <ErrorMessage
                      name='emailAddress'
                      render={(msg) => (
                        <div
                          className='text-danger'
                          data-testid='create-account-account-details-email-address-field-error'
                        >
                          {msg}
                        </div>
                      )}
                    />
                  </div>
                </div>

                <div className='row'>
                  <div className='form-group col-12 col-sm-12 col-md'>
                    <label htmlFor='mailingAddress'>Street Address</label>
                    <FastField
                      as='textarea'
                      id='mailingAddress'
                      name='mailingAddress'
                      data-testid='create-account-account-details-mailing-address-field'
                      placeholder='Optional'
                      className='form-control'
                    />
                    <ErrorMessage
                      name='mailingAddress'
                      render={(msg) => (
                        <div
                          className='text-danger'
                          data-testid='create-account-account-details-mailing-address-field-error'
                        >
                          {msg}
                        </div>
                      )}
                    />
                  </div>
                </div>

                <div className='row last-row'>
                  <div className='form-group col-md col-sm-12'>
                    <label htmlFor='zipCode'>Zip Code</label>
                    <FastField
                      type='text'
                      id='zipCode'
                      name='zipCode'
                      data-testid='create-account-account-details-zip-code-field'
                      placeholder='Optional'
                      className='form-control'
                    />
                    <ErrorMessage
                      name='zipCode'
                      render={(msg) => (
                        <div
                          className='text-danger'
                          data-testid='create-account-account-details-zip-code-field-error'
                        >
                          {msg}
                        </div>
                      )}
                    />
                  </div>
                  <div className='form-group col-md col-sm-12'>
                    <label htmlFor='city'>City</label>
                    <FastField
                      type='text'
                      id='city'
                      name='city'
                      data-testid='create-account-account-details-city-field'
                      placeholder='Optional'
                      className='form-control'
                    />
                    <ErrorMessage
                      name='city'
                      render={(msg) => (
                        <div
                          className='text-danger'
                          data-testid='create-account-account-details-city-field-error'
                        >
                          {msg}
                        </div>
                      )}
                    />
                  </div>
                  <Select
                    selectStyling='form-group col-md col-sm-12'
                    fieldStyling={
                      formik.errors.selectState
                        ? 'form-control is-invalid'
                        : 'form-control'
                    }
                    label='State'
                    name='selectState'
                    data-testid='create-account-account-details-select-state-field'
                    options={dropdownStateOptions}
                  >
                    <ErrorMessage
                      name='selectState'
                      render={(msg) => (
                        <div
                          className='text-danger'
                          data-testid='create-account-account-details-select-state-field-error'
                        >
                          {msg}
                        </div>
                      )}
                    />
                  </Select>
                </div>
              </div>

              <Button
                type='submit'
                className='btn-submit btn-next'
                data-testid='create-account-account-details-next-button'
              >
                Next
              </Button>
            </Form>
          )
        }}
      </Formik>
      {submitError && <div>{submitError}</div>}
    </>
  )
}

export default CourseTabPlaceOrderCreateAccountForm
